import { create } from '@bufbuild/protobuf'
import {
  QuestionTypeEnum,
  QuestionnaireItemSchema,
  QuestionnaireOptionSchema,
  QuestionnaireSchema,
  type Questionnaire,
  type QuestionnaireItem,
} from '@/gen/cortado/v1/questionnaires_pb'

export function sortedQuestionnaireItems(questionnaire?: Questionnaire | null): QuestionnaireItem[] {
  if (!questionnaire?.items.length) return []
  return [...questionnaire.items].sort((a, b) => a.order - b.order)
}

/** An item's answer as text (Yes/No, option labels or file names joined), or null when unanswered. */
export function formatQuestionnaireAnswer(item: QuestionnaireItem): string | null {
  const answer = item.answer?.value
  if (!answer?.case) return null

  switch (answer.case) {
    case 'text':
      return answer.value.trim() !== '' ? answer.value : null
    case 'boolean':
      return answer.value ? 'Yes' : 'No'
    case 'choice':
      return answer.value.optionLabel || answer.value.optionId || null
    case 'multiChoice':
      return answer.value.choices.length > 0
        ? answer.value.choices.map((choice) => choice.optionLabel || choice.optionId).join(', ')
        : null
    case 'files':
      return answer.value.files.length > 0
        ? answer.value.files.map((file) => file.filename || file.attachmentId).join(', ')
        : null
    default:
      return null
  }
}

export function itemHasAnswer(item: QuestionnaireItem): boolean {
  const answer = item.answer?.value
  if (!answer?.case) return false

  switch (answer.case) {
    case 'text':
      return answer.value.trim() !== ''
    case 'boolean':
      return true
    case 'choice':
      return answer.value.optionId.trim() !== ''
    case 'multiChoice':
      return answer.value.choices.length > 0
    case 'files':
      return answer.value.files.length > 0
    default:
      return false
  }
}

export type BookingQuestionnaireDraftOption = {
  id: string
  label: string
}

export type BookingQuestionnaireDraftItem = {
  id: string
  prompt: string
  description: string
  type: QuestionTypeEnum
  required: boolean
  options: BookingQuestionnaireDraftOption[]
}

export const BOOKING_QUESTION_TYPES = [
  { type: QuestionTypeEnum.FREE_FORM, label: 'Short text' },
  { type: QuestionTypeEnum.PARAGRAPH, label: 'Long text' },
  { type: QuestionTypeEnum.MULTIPLE_CHOICE, label: 'Multiple choice' },
  { type: QuestionTypeEnum.MULTI_SELECT, label: 'Multi-select' },
  { type: QuestionTypeEnum.BOOLEAN, label: 'Yes / No' },
  { type: QuestionTypeEnum.PHONE, label: 'Phone' },
  { type: QuestionTypeEnum.EMAIL, label: 'Email' },
  { type: QuestionTypeEnum.ADDRESS, label: 'Address' },
  { type: QuestionTypeEnum.FILE_UPLOAD, label: 'Single file upload' },
  { type: QuestionTypeEnum.FILE_UPLOAD_MULTIPLE, label: 'Multiple file upload' },
] as const

export function bookingQuestionTypeLabel(type: QuestionTypeEnum): string {
  return BOOKING_QUESTION_TYPES.find((entry) => entry.type === type)?.label ?? 'Short text'
}

export function bookingQuestionNeedsOptions(type: QuestionTypeEnum): boolean {
  return type === QuestionTypeEnum.MULTIPLE_CHOICE || type === QuestionTypeEnum.MULTI_SELECT
}

export type QuestionWidgetKind =
  | 'text'
  | 'textarea'
  | 'tel'
  | 'email'
  | 'address'
  | 'boolean'
  | 'choice'
  | 'multi'
  | 'files'

export function questionWidgetKind(type: QuestionTypeEnum): QuestionWidgetKind {
  switch (type) {
    case QuestionTypeEnum.PARAGRAPH:
      return 'textarea'
    case QuestionTypeEnum.PHONE:
      return 'tel'
    case QuestionTypeEnum.EMAIL:
      return 'email'
    case QuestionTypeEnum.ADDRESS:
      return 'address'
    case QuestionTypeEnum.BOOLEAN:
      return 'boolean'
    case QuestionTypeEnum.MULTIPLE_CHOICE:
      return 'choice'
    case QuestionTypeEnum.MULTI_SELECT:
      return 'multi'
    case QuestionTypeEnum.FILE_UPLOAD:
    case QuestionTypeEnum.FILE_UPLOAD_MULTIPLE:
      return 'files'
    default:
      return 'text'
  }
}

export function questionnaireItemCountLabel(count: number): string {
  if (count <= 0) return 'None'
  return count === 1 ? '1 question' : `${count} questions`
}

export function createDraftOption(
  label = '',
  createId: () => string = () => crypto.randomUUID(),
): BookingQuestionnaireDraftOption {
  return { id: createId(), label }
}

export function createDraftItem(
  overrides: Partial<BookingQuestionnaireDraftItem> = {},
  createId: () => string = () => crypto.randomUUID(),
): BookingQuestionnaireDraftItem {
  const type = overrides.type ?? QuestionTypeEnum.FREE_FORM
  return {
    id: overrides.id ?? createId(),
    prompt: overrides.prompt ?? '',
    description: overrides.description ?? '',
    type,
    required: overrides.required ?? true,
    options: overrides.options ?? (
      bookingQuestionNeedsOptions(type)
        ? [createDraftOption('Option 1', createId), createDraftOption('Option 2', createId)]
        : []
    ),
  }
}

export function withQuestionType(
  item: BookingQuestionnaireDraftItem,
  type: QuestionTypeEnum,
  createId: () => string = () => crypto.randomUUID(),
): BookingQuestionnaireDraftItem {
  if (!bookingQuestionNeedsOptions(type)) {
    return { ...item, type, options: [] }
  }
  if (bookingQuestionNeedsOptions(item.type) && item.options.length > 0) {
    return { ...item, type }
  }
  return {
    ...item,
    type,
    options: [createDraftOption('Option 1', createId), createDraftOption('Option 2', createId)],
  }
}

export function draftFromQuestionnaire(
  questionnaire: Questionnaire | undefined,
  createId: () => string = () => crypto.randomUUID(),
): BookingQuestionnaireDraftItem[] {
  if (!questionnaire?.items.length) return []

  return [...questionnaire.items]
    .sort((a, b) => a.order - b.order)
    .map((item) => {
      const type = item.type === QuestionTypeEnum.UNSPECIFIED
        ? QuestionTypeEnum.FREE_FORM
        : item.type
      return {
        id: item.id || createId(),
        prompt: item.prompt,
        description: item.description ?? '',
        type,
        required: item.required,
        options: item.options.map((option) => ({
          id: option.id || createId(),
          label: option.label,
        })),
      }
    })
}

export function questionnaireItemsFromDraft(
  items: BookingQuestionnaireDraftItem[],
): QuestionnaireItem[] {
  return items.map((item, index) => create(QuestionnaireItemSchema, {
    id: item.id,
    order: index,
    prompt: item.prompt.trim(),
    description: item.description.trim() || undefined,
    type: item.type,
    required: item.required,
    options: bookingQuestionNeedsOptions(item.type)
      ? item.options
        .filter((option) => option.label.trim() !== '')
        .map((option, optionIndex) => create(QuestionnaireOptionSchema, {
          id: option.id,
          label: option.label.trim(),
          displayOrder: optionIndex,
        }))
      : [],
  }))
}

export function questionnaireFromDraft(
  items: BookingQuestionnaireDraftItem[],
): Questionnaire | undefined {
  if (items.length === 0) return undefined

  return create(QuestionnaireSchema, {
    items: questionnaireItemsFromDraft(items),
  })
}

/**
 * Detaches draft items (and their options) from whatever they were copied
 * from by minting fresh local ids. Used by "copy from templates" so the
 * booking questionnaire never links back to the org/event-type template —
 * these ids won't match anything on the booking's existing questionnaire, so
 * the server always mints its own ids for them (see
 * `questionnaire.ReconcileBookingQuestionnaire`).
 */
export function cloneDraftItemsWithNewIds(
  items: BookingQuestionnaireDraftItem[],
  createId: () => string = () => crypto.randomUUID(),
): BookingQuestionnaireDraftItem[] {
  return items.map((item) => ({
    ...item,
    id: createId(),
    options: item.options.map((option) => ({ ...option, id: createId() })),
  }))
}

export function validateQuestionnaireDraft(
  items: BookingQuestionnaireDraftItem[],
): string | null {
  if (items.length === 0) return null

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index]
    const label = `Question ${index + 1}`
    if (item.prompt.trim() === '') {
      return `${label} needs a prompt`
    }
    if (item.type === QuestionTypeEnum.UNSPECIFIED) {
      return `${label} needs a type`
    }
    if (
      bookingQuestionNeedsOptions(item.type)
      && item.options.every((option) => option.label.trim() === '')
    ) {
      return `${label} needs at least one option`
    }
  }

  return null
}
